import React, { useEffect, useMemo, useState, useCallback } from "react";
import { Button } from "reactstrap";

const overlayStyle = {
  position: "fixed",
  inset: 0,
  background: "rgba(0,0,0,0.35)",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  zIndex: 10000,
};

const modalStyle = {
  background: "#fff",
  width: 560,
  maxWidth: "calc(100vw - 24px)",
  borderRadius: 10,
  padding: 16,
  boxShadow: "0 10px 30px rgba(0,0,0,0.18)",
};

const pillStyle = {
  display: "inline-block",
  padding: "2px 8px",
  borderRadius: 999,
  fontSize: 12,
  background: "#f1f3f5",
  color: "#495057",
  marginLeft: 8,
};

const normalizePF = (val) => {
  const v = (val ?? "").toString().trim().toLowerCase();
  if (v === "p" || v === "pass") return "p";
  if (v === "f" || v === "fail") return "f";
  return v;
};

const displayCurrentValue = (v) => {
  const s = (v ?? "").toString().trim();
  if (s === "-777") return "PASS";
  if (s === "-999") return "FAIL";
  return s;
};

const toDbValueLegacy = ({ rawInput, type }) => {
  const input = (rawInput ?? "").toString().trim();

  const isStar = (type ?? "").toString().includes("*");
  const isPass = (type ?? "").toString().toLowerCase().includes("pass");

  if (isPass && !isStar) {
    const v = normalizePF(input);
    if (v === "p") return "-777";
    if (v === "f") return "-999";
    return null;
  }

  if (isStar) {
    return input;
  }

  const n = Number(input);
  if (!Number.isFinite(n)) return null;
  return String(n);
};

const SupvOverrideModal = ({
  open,
  onClose,
  onConfirm,
  context,
}) => {
  const [newValue, setNewValue] = useState("");
  const [err, setErr] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setNewValue("");
    setErr("");
    setSaving(false);
  }, [open, context]);

  const type = useMemo(() => (context?.type ?? "").toString(), [context]);

  const currentDisplay = useMemo(
    () => displayCurrentValue(context?.currentValue),
    [context]
  );

  const placeholder = useMemo(() => {
    return type.toLowerCase().includes("pass") && !type.includes("*")
      ? "Enter P or F"
      : "Enter new value";
  }, [type]);

  const validateLegacy = useCallback(() => {
    const input = (newValue ?? "").toString().trim();
    if (!input) {
      setErr("Please enter a new value.");
      return false;
    }

    const isStar = type.includes("*");
    const isPass = type.toLowerCase().includes("pass");

    if (isPass && !isStar) {
      const v = normalizePF(input);
      if (!(v === "p" || v === "f")) {
        setErr("Please enter only P or F in a pass/fail dimension.");
        return false;
      }
      return true;
    }

    if (!isStar) {
      const n = Number(input);
      if (!Number.isFinite(n)) {
        setErr(
          "Invalid value detected here. Please try again or contact technical support on x1776"
        );
        return false;
      }
    }

    return true;
  }, [newValue, type]);

  const handleInputChange = useCallback((e) => {
    setNewValue(e.target.value);
    if (err) setErr("");
  }, [err]);

  const handleSave = useCallback(async () => {
    if (saving) return;

    setErr("");
    if (!validateLegacy()) return;

    const newDb = toDbValueLegacy({ rawInput: newValue, type });
    if (newDb == null) {
      setErr("Invalid value. Please enter a valid value.");
      return;
    }

    setSaving(true);
    try {
      const ok = await onConfirm?.(newDb);
      if (!ok) {
        setErr(
          "Error in updating this MAFIA entry. Has this entry already been locked? Please try again or contact technical support on x1776."
        );
        return;
      }

      onClose?.();
    } finally {
      setSaving(false);
    }
  }, [saving, validateLegacy, newValue, type, onConfirm, onClose]);

  if (!open) return null;

  return (
    <div style={overlayStyle} role="dialog" aria-modal="true">
      <div style={modalStyle}>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <h3 style={{ margin: 0 }}>Supervisor Override</h3>
          <span style={pillStyle}>Legacy Override</span>
        </div>

        <div style={{ marginTop: 10, fontSize: 13, color: "#6c757d" }}>
          <div>
            <b>Job:</b> {context?.jobNum} &nbsp; <b>Wave:</b>{" "}
            {context?.waveNumber}
          </div>
          <div>
            <b>DNA:</b> {context?.dnaNum} &nbsp; <b>Piece:</b>{" "}
            {context?.pieceNum} &nbsp; <b>Cell:</b> {context?.cellNum}
          </div>
          <div>
            <b>Rev:</b> {context?.revMajor}.{context?.revMinor} &nbsp;{" "}
            <b>Type:</b> {type || "—"}
          </div>
        </div>

        <div style={{ marginTop: 12 }}>
          <div style={{ fontSize: 13, marginBottom: 6 }}>
            Current Value:{" "}
            <b style={{ color: "#212529" }}>{currentDisplay || "—"}</b>
          </div>

          <label style={{ fontSize: 12, color: "#6c757d" }}>New Value</label>
          <input
            type="text"
            className="form-control"
            value={newValue}
            onChange={handleInputChange}
            placeholder={placeholder}
          />

          {err ? (
            <div style={{ marginTop: 8, color: "#dc3545", fontSize: 13 }}>
              {err}
            </div>
          ) : null}
        </div>

        <div
          style={{
            marginTop: 16,
            display: "flex",
            justifyContent: "flex-end",
            gap: 8,
          }}
        >
          <Button color="secondary" outline onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button color="primary" onClick={handleSave} disabled={saving}>
            {saving ? "Updating..." : "Update"}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default React.memo(SupvOverrideModal);